import React, { useCallback, useEffect } from "react";
import { Redirect, Route, Switch, useLocation } from "react-router-dom";

import AddIcon from "@mui/icons-material/Add";
import { Button, Stack } from "@mui/material";
import EmptyCardAnalytics from "assets/images/EmptyCardAnalytics.png";
import EmptyCardGrowth from "assets/images/EmptyCardGrowth.png";
import EmptyCardPortfolioAnalysisOverview from "assets/images/EmptyCardPortfolioAnalysisOverview.png";
import { AsyncContentMui, RoleComponentGuard } from "components";
import { AccessDenied } from "components/Access-denied/AccessDenied";
import { EmptyCard, Props as EmptyCardProps } from "components/Empty-card/EmptyCard";
import { DealCreate } from "components/modals";
import { QueryManager } from "components/reports";
import { EMPTY_CARD_CONTENT } from "constants/empty-card";
import { useActionHook, useCanCreateDeal, useNavigate, usePrompt } from "hooks";
import { useAccountSelector, usePickUserPermissions, useReportsActions, useSelectBookmarksByType } from "store";
import { IReportType, UserPermissionType } from "store/types";

import { checkReportType } from "../../components/guards/Default-view.guard";
import { RoleName } from "../../constants";
import { Routes } from "../../constants/routes";
import { CashflowModel, Snapshot, Timeline } from "../index";

type EmptyStateItem = Pick<EmptyCardProps, "title" | "description" | "image">;

const emptyStateConfig: Record<string, EmptyStateItem> = {
  [Routes.timeline]: {
    title: EMPTY_CARD_CONTENT.portfolioTimeline.title,
    description: {
      text: EMPTY_CARD_CONTENT.portfolioTimeline.description,
      externalText: EMPTY_CARD_CONTENT.portfolioTimeline.externalDescription,
      sx: { width: 484 }
    },
    image: { src: EmptyCardGrowth, sx: { width: 315, height: 140 } }
  },
  [Routes.cashflow]: {
    title: EMPTY_CARD_CONTENT.portfolioCashflow.title,
    description: {
      text: EMPTY_CARD_CONTENT.portfolioCashflow.description,
      externalText: EMPTY_CARD_CONTENT.portfolioCashflow.externalDescription,
      sx: { width: 484 }
    },
    image: { src: EmptyCardGrowth, sx: { width: 315, height: 140 } }
  },
  [Routes.snapshot]: {
    title: EMPTY_CARD_CONTENT.portfolioSnapshot.title,
    description: {
      text: EMPTY_CARD_CONTENT.portfolioSnapshot.description,
      externalText: EMPTY_CARD_CONTENT.portfolioSnapshot.externalDescription,
      sx: { width: 484 }
    },
    image: { src: EmptyCardPortfolioAnalysisOverview, sx: { width: 640, height: 243 } }
  },
  [Routes.classic]: {
    title: EMPTY_CARD_CONTENT.portfolioClassic.title,
    description: {
      text: EMPTY_CARD_CONTENT.portfolioClassic.description,
      externalText: EMPTY_CARD_CONTENT.portfolioClassic.externalDescription,
      sx: { width: 484 }
    },
    image: { src: EmptyCardAnalytics, sx: { width: 315, height: 140 } }
  }
};

export const PortfolioAnalysis: React.FC = () => {
  const routeComponents: Record<string, React.ReactNode> = {
    [Routes.timeline]: <Timeline />,
    [Routes.cashflow]: <CashflowModel />,
    [Routes.snapshot]: <Snapshot />,
    [Routes.classic]: <Snapshot />
  };
  const reportsBookmarks = useSelectBookmarksByType("report");
  const portfolioAnalysis = usePickUserPermissions("portfolio_analysis");
  const noAccessPermission = portfolioAnalysis && portfolioAnalysis === UserPermissionType.NO_ACCESS;
  const canCreateDeal = useCanCreateDeal();
  const navigate = useNavigate();
  const openPrompt = usePrompt();
  const { show_cashflow } = useAccountSelector() || {};
  const { pathname } = useLocation();

  const { getCheckMeasureDataAndDealsAction } = useReportsActions();
  const {
    action: checkMeasureData,
    data: hasData,
    loading: checkLoading
  } = useActionHook(getCheckMeasureDataAndDealsAction);

  useEffect(() => {
    checkMeasureData();
  }, [pathname]);

  const handleAddDeal = useCallback(async () => {
    const data = await openPrompt({ Component: DealCreate, props: {} });
    if (!data) return;
    navigate({ route: "deal", params: { deal_id: data.id } });
  }, [openPrompt, navigate]);

  const addDealButton = canCreateDeal ? (
    <Button variant="contained" onClick={handleAddDeal} startIcon={<AddIcon />} sx={{ bgcolor: "#071F35" }}>
      Add Deal
    </Button>
  ) : undefined;

  const defaultBookmark = reportsBookmarks.find(
    (item) => item.is_default && checkReportType(item.report_type, IReportType.PORTFOLIO)
  );

  const to = defaultBookmark ? defaultBookmark.params.split("?")[0] : Routes.snapshot;

  const routes = [Routes.timeline, ...(show_cashflow ? [Routes.cashflow] : []), Routes.snapshot, Routes.classic];

  const renderRoute = (route: string) => {
    const config = emptyStateConfig[route];

    return (
      <Route key={route} path={route}>
        {hasData ? routeComponents[route] : <EmptyCard {...config} actionsElement={addDealButton} />}
      </Route>
    );
  };

  return (
    <RoleComponentGuard
      availableRoles={[RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer]}
      hideChild={noAccessPermission}
      RenderInstead={<AccessDenied redirectClick={() => navigate({ route: "main" })} title="Back home" />}
    >
      <Stack
        sx={{
          alignItems: "stretch",
          flexGrow: 1,
          overflow: "hidden",
          bgcolor: "#f6f8fc"
        }}
      >
        <AsyncContentMui isLoading={checkLoading} spinnerProps={{ size: 150 }}>
          <QueryManager riskMetrics showFilters={Boolean(hasData)} />
          <Stack
            sx={{
              gap: "16px",
              overflow: "auto",
              flexGrow: 1,
              scrollMarginRight: "30px",
              pr: "2px",
              pl: "2px",
              marginX: "-2px"
            }}
          >
            <Switch>
              {routes.map(renderRoute)}
              <Redirect exact from={Routes.reports} to={to} />
            </Switch>
          </Stack>
        </AsyncContentMui>
      </Stack>
    </RoleComponentGuard>
  );
};
